import React, { useState, useEffect, useRef } from 'react';
import { X, Palette, LayoutGrid, Tag } from 'lucide-react';
import { colorOptions } from './noteStyles';
import CategorySelector from './CategorySelector';
import QuadrantSelector from './QuadrantSelector';
import DueDateSelector from './DueDateSelector';

const NoteSettings = ({
  isOpen,
  onClose,
  note,
  onSave,
  taskType = 'active'
}) => {
  const [localSettings, setLocalSettings] = useState({
    color: note.color,
    quadrant: note.quadrant,
    category: note.category,
    dueAt: note.dueAt
  });

  const contentRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setLocalSettings({
        color: note.color,
        quadrant: note.quadrant,
        category: note.category,
        dueAt: note.dueAt
      });
    }
  }, [isOpen, note]);

  const handleSave = () => {
    onSave(localSettings);
    onClose();
  };

  const handleColorChange = (newColor) => {
    setLocalSettings({ ...localSettings, color: newColor });
  };

  const handleQuadrantChange = (newQuadrant) => {
    setLocalSettings({ ...localSettings, quadrant: newQuadrant });
  };

  const handleCategoryChange = (newCategory) => {
    setLocalSettings({ ...localSettings, category: newCategory });
  };

  const handleDueDateChange = (newDueAt) => {
    setLocalSettings({ ...localSettings, dueAt: newDueAt });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-96 max-w-full m-4 flex flex-col max-h-[90vh]">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">Note Settings</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>
        <div ref={contentRef} className="p-4 overflow-y-auto flex-grow">
          <div className="mb-4">
            <label className="flex items-center mb-2 text-sm font-medium text-gray-700">
              <Palette size={20} className="mr-2" />
              Note Color
            </label>
            <div className="grid grid-cols-5 gap-2">
              {colorOptions.map((color) => (
                <button
                  key={color.class}
                  className={`w-full h-8 rounded ${color.class} ${localSettings.color === color.class ? 'ring-2 ring-purple-500' : ''}`}
                  onClick={() => handleColorChange(color.class)}
                  title={color.name}
                />
              ))}
            </div>
          </div>
          <div className="mb-4">
            <label className="flex items-center mb-2 text-sm font-medium text-gray-700">
              <LayoutGrid size={20} className="mr-2" />
              Quadrant
            </label>
            <QuadrantSelector
              selectedQuadrant={localSettings.quadrant}
              onQuadrantChange={handleQuadrantChange}
            />
          </div>
          <div className="mb-4">
            <label className="flex items-center mb-2 text-sm font-medium text-gray-700">
              <Tag size={20} className="mr-2" />
              Category
            </label>
            <CategorySelector
              selectedCategory={localSettings.category}
              onSelectCategory={handleCategoryChange}
            />
          </div>

          <DueDateSelector
            dueAt={localSettings.dueAt}
            onChange={handleDueDateChange}
            onClear={() => handleDueDateChange(null)}
            isScheduled={taskType === 'scheduled'}
            className="mb-4"
          />
        </div>
        <div className="flex justify-end space-x-2 p-4 border-t">
          <button
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            onClick={handleSave}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default NoteSettings;